"use client";

// React & Nextjs imports
import Image from "next/image";
import React, { useState, useRef, useMemo, useEffect } from "react";

// Third party imports
import moment from "moment";
import { enIN } from "date-fns/locale/en-IN";
import { format, subDays } from "date-fns";
import { format as dateFnsTz } from "date-fns-tz";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

// Models & Constants
import {
    ChartData,
    durationConfig,
    priceGraphPeriod,
    PeriodType,
    UsStock,
} from "@/app/interface/global";
import { CountryCurrency } from "@/app/interface/currency";

// Hooks & Utils & Contexts
import { useCulture } from "@/context/cultureProvider";
import { formatCurrency } from "@/app/utils/currency-formatter";
import { useUsStockChartQuery, UsStockChartProps } from "@/app/global/use-us-stocks";
import { getPriceGraphData, handlePriceGraphParams } from "@/app/utils/us-stocks";

// Components
import TradingViewChart from "@/app/global/components/candle-chart";
import { themes } from "@/app/enums/chart-themes";

// UI Components
import { Badge, BadgeScheme, BadgeVariant } from "@/stories/badge/badge";

// Icons
import TopPicks from "@/app/assets/icons/top-picks";
import { ChartLineIcon /* , Info */ } from "lucide-react";

const chartType = {
    LINE_CHART: "LINE_CHART",
    CANDLE_CHART: "CANDLE_CHART",
} as const;

type ChartTypes = keyof typeof chartType;

type ChartDataPoint = {
    stroke: string;
    strokeWidth: number;
    dataKey: string;
    name: string;
    color: string;
    value: number;
    payload: {
        x: number;
        y: number;
    };
    hide: boolean;
};

type Tooltips = {
    active?: boolean;
    payload?: ChartDataPoint[];
    label?: string;
    culture: CountryCurrency;
};

type Cursor = {
    x: number;
    y: number;
};

type CursorParent = {
    points: Cursor[];
};

type Props = {
    stockDetails: UsStock;
    isMobileWidgetOpen?: boolean;
};

const CustomCursor = ({ points }: CursorParent) => {
    return (
        <line
            x1={points[0].x}
            y1={0}
            x2={points[0].x}
            y2="100%"
            stroke="#6B7280"
            strokeWidth="1"
            strokeDasharray="3 3"
        />
    );
};

const Viz: React.FC<Props> = ({ stockDetails /* , isMobileWidgetOpen */ }) => {
    /**
     * Show 1 day graph
     */
    const defaultChartParams = {
        symbol: stockDetails?.symbol || "",
        from: format(subDays(new Date(), 1), "yyyy-MM-dd"),
        to: format(new Date(), "yyyy-MM-dd"),
        resolution: String(durationConfig[priceGraphPeriod["1D"]].days),
    };

    const { cultureContext } = useCulture();

    const [period, setPeriod] = useState<PeriodType>(priceGraphPeriod["1D"]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [toggleChart, setToggleChart] = useState<ChartTypes>(chartType.LINE_CHART);
    const [chartParams, setChartParams] = useState<UsStockChartProps>(defaultChartParams);
    const [priceChartData, setPriceChartData] = useState<ChartData[] | []>([]);

    const LineChartContainerRef = useRef<HTMLDivElement>(null);
    const CandleChartContainerRef = useRef<HTMLDivElement>(null);

    const { usStockChart, isUsStockChartLoading } = useUsStockChartQuery({ ...chartParams });

    useEffect(() => {
        if (usStockChart?.data?.graph_ohlc && usStockChart.data.graph_ohlc.length > 0) {
            const data: ChartData[] | undefined = getPriceGraphData(usStockChart.data.graph_ohlc);
            if (data) {
                setPriceChartData(data);
            }
        }
    }, [usStockChart?.data?.graph_ohlc]);

    const priceDiff = useMemo(() => {
        const value = Number(stockDetails?.price_movement?.one_day_percentage);

        if (isNaN(value)) {
            return 0;
        }

        return value;
    }, [stockDetails?.price_movement?.one_day_percentage]);

    const CustomTooltip = ({ active, payload, label, culture }: Tooltips) => {
        if (active && payload && payload.length) {
            return (
                <div className="shadow-xs mt-4 rounded bg-[#E8E9ED] px-2 py-1.5 text-sm md:mt-1">
                    <div className="text-[#494F6E]">
                        {formatCurrency(payload[0].value, culture.currencyCode, culture.locale)} |{" "}
                        {moment(label).format(
                            `DD/MM/yyyy ${period === priceGraphPeriod["1D"] || period === priceGraphPeriod["1W"] ? "hh:mm a" : ""}`,
                        )}
                    </div>
                </div>
            );
        }
        return null;
    };

    const formatXAxis = (timestamp: number) => {
        return moment(timestamp).format("dd/MM/YY");
    };

    const formatYAxis = (price: number, symbol: string) => {
        return symbol + price;
    };

    const handlePeriodChange = (duration: PeriodType) => {
        if (duration === "--") {
            setIsLoading(true);
            if (toggleChart === chartType.CANDLE_CHART) {
                setToggleChart(chartType.LINE_CHART);
                setTimeout(() => {
                    setIsLoading(false);
                }, 1000);
            } else {
                setToggleChart(chartType.CANDLE_CHART);
            }
        } else {
            setToggleChart(chartType.LINE_CHART);
        }

        if (duration !== "--") {
            setPeriod(duration);
            const params = handlePriceGraphParams(stockDetails?.symbol, duration);

            if (params) {
                setChartParams({ ...params });
            }
        }
    };

    const getAbbreviatedTimeZoneName = useMemo(() => {
        // Get the current date in the 'Asia/Kolkata' timezone
        const nowInKolkata = new Date(); // JavaScript Date objects are inherently in local time
        const timeZone = "Asia/Kolkata";

        // Format the date using the 'en-IN' locale
        // The format specifier 'zzz' is used for the abbreviated time zone name
        return dateFnsTz(nowInKolkata, "MMMM d, yyyy h:mm aaa zzz", {
            timeZone,
            locale: enIN,
        });
    }, []);

    return (
        <div className="rounded-3xl bg-white sm:border sm:border-gray-200 sm:p-6 sm:pb-0 md:min-h-[502px]">
            {" "}
            {/* sm:w-[845px] */}
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <div className="flex items-center">
                        <span className="pl-0 text-3xl font-semibold md:text-4xl">
                            {formatCurrency(
                                stockDetails?.live_price ?? 0,
                                cultureContext.currencyCode,
                                cultureContext.locale,
                            )}
                        </span>
                    </div>
                    <div className="flex items-center">
                        <span className="font-weight-bold"></span>
                        <span
                            className={`detail my-auto ml-3 flex items-center pb-0 text-lg font-semibold md:text-3xl ${priceDiff < 0 ? "text-red-600" : "text-[#047857]"} `}
                        >
                            {priceDiff ? (
                                priceDiff < 0 ? (
                                    <>
                                        <span className="mr-0.5 inline-block text-xs">▼</span>
                                        {`${priceDiff}%`}
                                    </>
                                ) : (
                                    priceDiff > 0 && (
                                        <>
                                            <span className="mr-0.5 inline-block text-xs">▲</span>
                                            {`${priceDiff}%`}
                                        </>
                                    )
                                )
                            ) : (
                                "--"
                            )}
                            &nbsp;
                            <span className="text-[#9195A9]">1D</span>
                        </span>
                    </div>
                    {/* <div className="relative ml-2 flex items-center">
                        <button className="group">
                            <Info
                                width="16"
                                height="16"
                                className={`${isMobileWidgetOpen ? "text-transparent" : "text-neutral-300"} cursor-pointer`}
                            />
                            <div className="absolute left-1/2 top-5 z-10 mt-2 hidden w-[300px] -translate-x-[60%] rounded-md bg-black p-4 text-sm text-white group-hover:block sm:left-1/2 sm:mx-auto sm:-translate-x-1/2 md:left-1/2 md:w-[400px] md:-translate-x-1/2">
                                The historical prices shown on the InCred Money platform have been
                                arrived at by using a combination of internal and external sources
                                which reflect the demand and supply situation that is further based
                                on factors like Financial performance, IPO related news, etc. These
                                prices are indicative in nature. We do not recommend investment
                                decisions taken solely based on the historical prices mentioned on
                                our platform.
                            </div>
                        </button>
                    </div> */}
                </div>
                <div className="hidden sm:block">
                    {false && (
                        <Badge
                            label={"Top pick"}
                            showIcon={false}
                            icon={<TopPicks />}
                            variant={BadgeVariant.Success}
                            scheme={BadgeScheme.Light}
                            showCross={false}
                            className="rounded-lg"
                        />
                    )}
                </div>
            </div>
            <div className="text-xs font-medium tracking-[0%] text-[#494F6E] md:text-base md:leading-[160%]">
                {`as on ${getAbbreviatedTimeZoneName}`}
            </div>
            <section
                className={`transition-all duration-200 ease-in sm:w-full md:ml-0 ${toggleChart === chartType.LINE_CHART ? "-ml-6 -mt-0 h-[300px] w-[100vw] md:mt-5" : "-ml-3 mt-5 h-[400px] w-[95vw]"}`}
            >
                {toggleChart === chartType.LINE_CHART && (
                    <>
                        {!isUsStockChartLoading ? (
                            <>
                                {priceChartData?.length > 0 ? (
                                    <div
                                        ref={LineChartContainerRef}
                                        className={`viz-section-width h-full w-full ${!isLoading ? "opacity-100" : "opacity-0"}`}
                                    >
                                        <ResponsiveContainer>
                                            <LineChart
                                                data={priceChartData}
                                                margin={{ top: 50, right: 20, bottom: 5, left: 0 }}
                                            >
                                                <XAxis
                                                    dataKey="x"
                                                    tickFormatter={formatXAxis}
                                                    hide={true}
                                                />
                                                <YAxis
                                                    dataKey="y"
                                                    tickFormatter={(price) => {
                                                        return formatYAxis(
                                                            price,
                                                            cultureContext.symbol,
                                                        );
                                                    }}
                                                    hide={false}
                                                    tick={{
                                                        fill: "#9195A9",
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                    }}
                                                    axisLine={false}
                                                    tickLine={false}
                                                    domain={["0", "auto"]}
                                                    padding={{ top: 0, bottom: 0 }}
                                                />
                                                <Tooltip
                                                    content={
                                                        <CustomTooltip culture={cultureContext} />
                                                    }
                                                    cursor={<CustomCursor points={[]} />}
                                                    position={{ y: 5 }}
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="y"
                                                    stroke={themes.incred.primary}
                                                    strokeWidth={2}
                                                    dot={false}
                                                    activeDot={{ r: 8 }}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center pb-10 text-sm tracking-wide">
                                        No data found. Select greater duration.
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="max-w-[90%] mx-auto flex h-full animate-pulse items-center justify-center rounded-2xl bg-gray-200 pb-8 md:mx-0 md:max-w-full w-full">
                                {/* <div className="h-6 w-[40%] animate-pulse rounded-3xl bg-gray-200"></div> */}
                            </div>
                        )}
                    </>
                )}
                {toggleChart === chartType.CANDLE_CHART && (
                    <div ref={CandleChartContainerRef} className="h-full w-full overflow-hidden">
                        <TradingViewChart stockDetails={stockDetails} />
                    </div>
                )}
            </section>
            <div className="flex items-center justify-between pb-4 pt-6 sm:ml-10 sm:mt-3 sm:py-3 md:mr-1">
                {Object.entries(durationConfig).map((duration) => {
                    const key = duration[0] as PeriodType;

                    return (
                        <button
                            key={key}
                            className={`flex h-[32px] w-[91px] items-center justify-center rounded-round text-xs font-medium text-[#0F1016] hover:bg-gray-100 ${period === key ? "active bg-[#F15D2A1A]" : "text-[#9195A9]"} ${key !== "--" && toggleChart === chartType.CANDLE_CHART ? "opacity-50" : ""}`}
                            onClick={() => handlePeriodChange(key as PeriodType)}
                            disabled={toggleChart === chartType.CANDLE_CHART && key !== "--"}
                        >
                            {key !== "--" ? (
                                <>{key}</>
                            ) : (
                                <>
                                    {toggleChart === chartType.CANDLE_CHART ? (
                                        <ChartLineIcon className="h-[18px] text-orange-500" />
                                    ) : (
                                        <Image
                                            src="https://s3.ap-south-1.amazonaws.com/assets.incredmoney.com/app/images/us+stocks/stock-candles.png"
                                            width={20}
                                            height={10}
                                            alt=""
                                            className="h-[14px] w-auto"
                                        />
                                    )}
                                </>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default Viz;
